import memoizeFormatConstructor from "intl-format-cache";
import { store } from "../../../../acc-boot/acc-boot.js";
import { noDecimalMarkets } from "./constants.js";

const _memoizeCache = {
  NUMBER_FORMAT: memoizeFormatConstructor(Intl.NumberFormat),
  DATE_FORMAT: memoizeFormatConstructor(Intl.DateTimeFormat),
};

/**
 * Formats a date into a short month and short year. Eg: Jan 19
 * @param {Date} date
 * @param {String} locale
 * @return {String}
 */
export function formatDateMonYY(date, locale) {
  const options = {
    year: "2-digit",
    month: "short",
  };
  const formatter = _memoizeCache.DATE_FORMAT(locale, options);
  return formatter.format(date).toUpperCase();
}

/**
 * Formats a js date to a date string as per timezone. Eg: 20/01/2020(MM/DD/YYYY)
 * @param {Date} date
 * @param {String} locale
 * @return {String}
 */
export function formatDate(date, locale) {
  const options = {
    timeZone: Intl.DateTimeFormat().resolvedOptions().timeZone,
    month: "2-digit",
    day: "2-digit",
    year: "numeric",
  };
  const formatter = _memoizeCache.DATE_FORMAT(locale, options);
  return formatter.format(date);
}

/**
 * Formats a date into a short month and full year. Eg: Jan 2019
 * @param {Date} date
 * @param {String} locale
 * @return {String}
 */
export function formatDateMonYYYY(date, locale) {
  const options = {
    year: "numeric",
    month: "short",
  };
  const formatter = _memoizeCache.DATE_FORMAT(locale, options);
  return formatter.format(date);
}

/**
 * Formats a date into a long month and long year. Eg: January 2019
 * @param {Date} date
 * @param {String} locale
 * @return {String}
 */
export function formatDateMonthYYYY(date, locale) {
  const options = {
    year: "numeric",
    month: "long",
  };
  const formatter = _memoizeCache.DATE_FORMAT(locale, options);
  return formatter.format(date);
}

/**
 * Formats a date into a short month. Eg: Jan
 * @param {Date} date
 * @param {String} locale
 * @return {String}
 */
export function formatDateMon(date, locale) {
  const options = {
    month: "short",
  };
  const formatter = _memoizeCache.DATE_FORMAT(locale, options);
  return formatter.format(date);
}

export function formatDateMMMDD(date, locale) {
  const options = {
    month: "long",
    day: "2-digit",
  };
  const formatter = _memoizeCache.DATE_FORMAT(locale, options);
  return formatter.format(date);
}

/**
 * Formats a date into a long month. Eg: January
 * @param {Date} date
 * @param {String} locale
 * @return {String}
 */
export function formatDateMonth(date, locale) {
  const options = {
    month: "long",
  };
  const formatter = _memoizeCache.DATE_FORMAT(locale, options);
  return formatter.format(date);
}

/**
 * Formats a date into a short year. Eg: 19
 * @param {Date} date
 * @param {String} locale
 * @return {String}
 */
export function formatDateYY(date, locale) {
  const options = {
    year: "2-digit",
  };
  const formatter = new Intl.DateTimeFormat(locale, options);
  return formatter.format(date);
}

/**
 * Formats a date into a long year. Eg: 2020
 * @param {Date} date
 * @param {String} locale
 * @return {String}
 */
export function formatDateYYYY(date, locale) {
  const options = {
    year: "numeric",
  };
  const formatter = _memoizeCache.DATE_FORMAT(locale, options);
  return formatter.format(date);
}

export function formatDateMMDD(date, locale) {
  const options = {
    month: "2-digit",
    day: "2-digit",
  };
  const formatter = _memoizeCache.DATE_FORMAT(locale, options);
  return formatter.format(date);
}

/**
 * Formats a number as a percentage.
 * @param {Number} number
 * @param {String} locale
 * @return {String}
 */
export function formatPercentage(number, locale) {
  const options = {
    style: "percent",
  };
  const formatter = _memoizeCache.NUMBER_FORMAT(locale, options);
  return formatter.format(number);
}

/**
 * Formats a number as a currency.
 * @param {Number} value
 * @param {String} currencyCode
 * @param {String} locale
 * @param {String} market
 * @param {Object} opts
 * @return {String}
 */
export function formatCurrency(value, currencyCode, locale, market, opts = {}) {
  const getCurrencyDisplayFromStore = () => {
    let currencyDisplay = "default";
    if (store && store.getState().configuration && store.getState().configuration.currencyDisplay) {
      currencyDisplay = store.getState().configuration.currencyDisplay;
    } else if (
      this &&
      this.accBoot &&
      this.accBoot.store &&
      this.accBoot.store.getState().configuration &&
      this.accBoot.store.getState().configuration.currencyDisplay
    ) {
      currencyDisplay = this.accBoot.store.getState().configuration.currencyDisplay;
    }
    return currencyDisplay;
  };

  let optionsObject = {
    style: "currency",
    currency: currencyCode,
    ...opts,
  };

  const alternateCurrencyDisplay = getCurrencyDisplayFromStore();

  if (alternateCurrencyDisplay !== "default") {
    optionsObject = { ...optionsObject, currencyDisplay: alternateCurrencyDisplay };
  }

  // Don't show decimals for Japan and Korea
  if ((market === "JP" && currencyCode === "JPY") || (market === "KR" && currencyCode === "KRW")) {
    optionsObject.maximumFractionDigits = 0;
    optionsObject.minimumFractionDigits = 0;
  }

  const formatter = _memoizeCache.NUMBER_FORMAT(locale, optionsObject);
  return formatter.format(value);
}

/**
 * Formats a number as ABO PV.
 * @param {Number} number - PV number to format
 * @param {String} locale - Locale of the user
 * @param {String} market - ISO Alpha 2 country code of the user's market
 * @param {Object} opts - Options to pass to the Intl format constructor. Overrides default and market-specific options.
 * @return {String}
 */
export function formatAboPV(number, locale, market, opts = {}) {
  let num = number;

  // Options that are applied by default
  const defaultOpts = {
    maximumFractionDigits: 2,
    minimumFractionDigits: 2,
  };

  // Market-specific options
  const marketOpts = {};

  // Don't show decimals for for Japan and Korea
  if (noDecimalMarkets.includes(market)) {
    marketOpts.maximumFractionDigits = 0;
    marketOpts.minimumFractionDigits = 0;
  }

  // Merge default, market-specific, and passed in options
  const options = { ...defaultOpts, ...marketOpts, ...opts };

  // If we are hiding decimals, we want to round down to avoid over-representing volume numbers.
  if (options.maximumFractionDigits === 0 && options.minimumFractionDigits === 0) {
    num = Math.floor(num);
  }

  const formatter = _memoizeCache.NUMBER_FORMAT(locale, options);
  return formatter.format(num);
}

/**
 * Formats a number as ABO BV.
 * @param {Number} number - BV number to format
 * @param {String} locale - Locale of the user
 * @param {String} market - ISO Alpha 2 country code of the user's market
 * @param {Object} opts - Options to pass to the Intl format constructor. Overrides default and market-specific options.
 * @return {String}
 */
export function formatAboBV(number, locale, market, opts = {}) {
  return formatAboPV(number, locale, market, opts);
}

/**
 * Formats a number as requirement volume.
 * @param {Number} number - Volume number to format
 * @param {String} locale - Locale of the user
 * @param {String} market - ISO Alpha 2 country code of the user's market
 * @param {Object} opts - Options to pass to the Intl format constructor. Overrides default and market-specific options.
 * @return {String}
 */

export function formatReqVolume(number, locale, _market, opts = {}) {
  let num = number;

  // Options that are applied by default
  const defaultOpts = {
    maximumFractionDigits: 0,
    minimumFractionDigits: 0,
  };

  // Market-specific options
  const marketOpts = {};

  // Market-specific options go here...

  // Merge default, market-specific, and passed in options
  const options = { ...defaultOpts, ...marketOpts, ...opts };

  const formatter = _memoizeCache.NUMBER_FORMAT(locale, options);
  return formatter.format(num);
}

export function formatInteger(number, locale, opts = {}) {
  // Options that are applied by default
  const defaultOpts = {
    maximumFractionDigits: 0,
    minimumFractionDigits: 0,
  };

  const options = { ...defaultOpts, ...opts };

  const formatter = _memoizeCache.NUMBER_FORMAT(locale, options);
  return formatter.format(number);
}

/**
 * Formats a date as a timestamp.
 * @param {Date} date
 * @param {String} locale
 * @return {String}
 */
export function formatTimestamp(date, locale) {
  const options = {
    hour: "numeric",
    minute: "numeric",
    second: "numeric",
    timeZone: Intl.DateTimeFormat().resolvedOptions().timeZone,
    timeZoneName: "short",
    month: "2-digit",
    day: "2-digit",
    year: "numeric",
  };
  return new Date(date).toLocaleDateString(locale, options);
}

/**
 * Returns the default currency code for a given market.
 * @param {String} market
 * @return {String}
 */
export function defaultCurrencyCode(market) {
  const fallbackCurrency = "USD";
  const currencyByMarket = {
    AR: "ARS",
    AT: "EUR",
    AU: "AUD",
    BE: "EUR",
    BG: "BGN",
    BN: "BND",
    BR: "BRL",
    BW: "BWP",
    CA: "CAD",
    CH: "CHF",
    CL: "CLP",
    CN: "CNY",
    CO: "COP",
    CR: "CRC",
    CZ: "CZK",
    DE: "EUR",
    DK: "DKK",
    DO: "DOP",
    EE: "EUR",
    ES: "EUR",
    FI: "EUR",
    FR: "EUR",
    GB: "GBP",
    GR: "EUR",
    GT: "GTQ",
    HK: "HKD",
    HN: "HNL",
    HR: "EUR",
    HU: "HUF",
    ID: "IDR",
    IE: "EUR",
    IN: "INR",
    IT: "EUR",
    JP: "JPY",
    KO: "KRW",
    KG: "KZT",
    KR: "KRW",
    KZ: "KZT",
    LT: "EUR",
    LV: "EUR",
    MN: "MNT",
    MX: "MXN",
    MY: "MYR",
    NA: "NAD",
    NL: "EUR",
    NO: "NOK",
    NZ: "NZD",
    PA: "PAB",
    PH: "PHP",
    PL: "PLN",
    PT: "EUR",
    RO: "RON",
    RU: "RUB",
    SE: "SEK",
    SG: "SGD",
    SI: "EUR",
    SK: "EUR",
    SV: "USD",
    TH: "THB",
    TR: "TRY",
    TW: "TWD",
    UA: "UAH",
    UK: "GBP",
    US: "USD",
    UY: "UYU",
    UZ: "UZS",
    VE: "VES",
    VN: "VND",
    ZA: "ZAR",
  };
  return currencyByMarket[market] || fallbackCurrency;
}

/**
 * This function gets the country name for a locale and country
 * @param {String} locale
 * @param {String} country
 * @returns {String} country name
 */
export function countryDisplayName(locale, country) {
  return new Intl.DisplayNames(locale, { type: "region" }).of(country);
}

/**
 * This function gets the currency name for a locale and currency
 * @param {String} locale
 * @param {String} currency
 * @returns {String} currency name
 */
export function currencyDisplayName(locale, currency) {
  return new Intl.DisplayNames(locale, { type: "currency" }).of(currency);
}

/**
 * This function format performance year
 * @param {String} date
 * @param {String} locale
 * @param {String} long return 4 digit YYYY
 *  @return {String}
 */
export function formatPY(date, locale, long) {
  const formatPerformanceYear = long ? formatDateYYYY(date, locale) : formatDateYY(date, locale);
  const endPos = long ? 4 : 2;
  return locale === "zh-CN" || locale === "ko-KR" ? formatPerformanceYear.slice(0, endPos) : formatPerformanceYear;
}
